import { useState, useCallback, useEffect } from 'react';
import { useReCaptcha } from './useReCaptcha';
import { verifyReCaptcha } from '../services/recaptcha';
import { handleRecaptchaError, type RecaptchaError } from '../utils/recaptcha';
import { useReCaptchaContext } from '../contexts/ReCaptchaContext';

export function useGlobalReCaptcha() {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<RecaptchaError | null>(null);
  const { getReCaptchaToken } = useReCaptcha();
  const { isEnabled, isLoading: configLoading } = useReCaptchaContext();

  const verifyUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Skip reCAPTCHA verification if disabled in config
      if (!isEnabled) {
        console.log('reCAPTCHA verification bypassed - disabled in config');
        setIsVerified(true);
        setIsLoading(false);
        return;
      }

      console.log('Starting reCAPTCHA verification...');
      
      // Get token with retry mechanism
      let token: string | null = null;
      let retryCount = 0;
      const maxRetries = 3;
      const initialDelay = 2000; // 2 seconds

      while (!token && retryCount < maxRetries) {
        try {
          console.log(`Attempting to get reCAPTCHA token (attempt ${retryCount + 1}/${maxRetries})`);
          token = await getReCaptchaToken('initial_verification');
          console.log('reCAPTCHA token obtained:', token ? 'Yes' : 'No');
          
          if (!token) {
            throw new Error('Empty token received');
          }

          // Verify token
          console.log('Verifying reCAPTCHA token...');
          const verified = await verifyReCaptcha(token, 'initial_verification');
          console.log('reCAPTCHA verification result:', verified);

          setIsVerified(verified);
          if (!verified) {
            console.error('reCAPTCHA verification failed');
            setError(handleRecaptchaError(new Error('Verification failed')));
          }
          return;
        } catch (tokenError) {
          console.error(`Token retrieval attempt ${retryCount + 1} failed:`, tokenError);
          retryCount++;
          if (retryCount < maxRetries) {
            const delay = Math.min(initialDelay * Math.pow(2, retryCount), 10000);
            console.log(`Waiting ${delay}ms before next attempt...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      throw new Error('Failed to obtain reCAPTCHA token after multiple attempts');
    } catch (error) {
      console.error('ReCAPTCHA verification error:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      setError(handleRecaptchaError(error));
      setIsVerified(false);
    } finally {
      setIsLoading(false);
    }
  }, [getReCaptchaToken, isEnabled]);

  useEffect(() => {
    let mounted = true;
    let retryTimeout: NodeJS.Timeout;

    const initVerification = async () => {
      if (!mounted) return;

      // Wait for config to load
      if (configLoading) {
        return;
      }

      try {
        await verifyUser();
      } catch (err) {
        console.error('Verification attempt failed:', err);
        setError(handleRecaptchaError(err));
      }
    };

    // Add a longer initial delay before verification
    console.log('Scheduling initial reCAPTCHA verification...');
    setTimeout(initVerification, 2000);

    return () => {
      mounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [verifyUser, configLoading]);

  return { isVerified, isLoading: isLoading || configLoading, error };
}